import { registerApplication } from "single-spa";
import { AUL_REG_PATH, BASE_APP_PATH, PASSWORD_RECOVERY_ROUTE_NAME, REGISTRATION_ROUTE_NAME, } from "../constants/routes.constants";
const CSI_FORM_APP_NAME = "@platform/mb-app-csi-form";
const CHAT_SUPPORT_MODULE_NAME = "@platform/mb-app-chat-support-ui";
const LEGAL_ENTITY_QUESTIONNARE_MODULE_NAME = "@platform/mb-app-xbpm-lep";
const REGISTRATION_PATHS = [
    REGISTRATION_ROUTE_NAME,
    PASSWORD_RECOVERY_ROUTE_NAME,
];
const excludeRegistrationSteps = (location) => REGISTRATION_PATHS.every((path) => !location.pathname.startsWith(path));
export function initApps(applications, context) {
    window.addEventListener("single-spa:first-mount", async () => {
        Object.entries(applications).forEach(([name, app]) => {
            const module = `@platform/${name}`;
            if ([
                CHAT_SUPPORT_MODULE_NAME,
                CSI_FORM_APP_NAME,
                LEGAL_ENTITY_QUESTIONNARE_MODULE_NAME,
            ].includes(module))
                return;
            const paths = Array.isArray(app.path)
                ? app.path
                : [app.path, `${BASE_APP_PATH}${app.path}`].filter((p) => p);
            const regExpMap = paths.map((path) => new RegExp("^(" + path + "/(.*))|(" + path + ")$"));
            registerApplication({
                name: module,
                app: () => System.import(module),
                activeWhen: (location) => regExpMap.some((regex) => regex.test(location.pathname)),
                customProps: {
                    ...context,
                    appConfig: app.config,
                    domElement: document.getElementById("apps-section"),
                },
            });
        });
    });
}
export function initCryptoApiTestApp(context) {
    registerApplication({
        name: "@platform/mb-app-cryptoapitest",
        app: () => System.import("@platform/mb-app-cryptoapitest"),
        activeWhen: "/cryptoapitest",
        customProps: {
            ...context,
            domElement: document.getElementById("apps-section"),
        },
    });
}
export function initCACheckApp(context) {
    registerApplication({
        name: "@platform/mb-app-ca-check",
        app: () => System.import("@platform/mb-app-ca-check"),
        activeWhen: "/ca-check",
        customProps: {
            ...context,
            domElement: document.getElementById("apps-section"),
        },
    });
}
export function initSupportChat(applications, context) {
    const chatApp = applications?.["mb-app-chat-support-ui"];
    if (!chatApp)
        return;
    registerApplication({
        name: CHAT_SUPPORT_MODULE_NAME,
        app: () => System.import(System.resolve(CHAT_SUPPORT_MODULE_NAME)),
        activeWhen: (location) => [...REGISTRATION_PATHS, BASE_APP_PATH].some((path) => !location.pathname.includes(path)),
        customProps: {
            ...context,
            appConfig: chatApp.config,
        },
    });
}
export function initLEQ(applications, context) {
    const LEQApp = applications?.["mb-app-xbpm-lep"];
    if (!LEQApp)
        return;
    registerApplication({
        name: LEGAL_ENTITY_QUESTIONNARE_MODULE_NAME,
        app: () => System.import(System.resolve(LEGAL_ENTITY_QUESTIONNARE_MODULE_NAME)),
        activeWhen: (location) => [LEQApp.path, AUL_REG_PATH].some((path) => location.pathname.startsWith(path)),
        customProps: {
            ...context,
            appConfig: LEQApp.config,
            domElement: document.getElementById("apps-section"),
        },
    });
}
export function initCSIForm(applications, context) {
    const app = applications?.["mb-app-csi-form"];
    if (!app)
        return;
    registerApplication({
        name: CSI_FORM_APP_NAME,
        app: () => System.import(System.resolve(CSI_FORM_APP_NAME)),
        activeWhen: (location) => [...REGISTRATION_PATHS].every((path) => !location.pathname.includes(path)),
        customProps: {
            ...context,
        },
    });
}
export function initConstantApps(menu, context) {
    registerApplication({
        name: "@platform/mb-app-header-react",
        app: () => System.import("@platform/mb-app-header-react"),
        activeWhen: (location) => excludeRegistrationSteps(location),
        customProps: {
            ...context,
            menu,
        },
    });
    registerApplication({
        name: "@platform/mb-app-subheader",
        app: () => System.import("@platform/mb-app-subheader"),
        activeWhen: (location) => excludeRegistrationSteps(location),
        customProps: {
            ...context,
            menu,
        },
    });
    registerApplication({
        name: "@platform/mb-app-main-grid",
        app: () => System.import("@platform/mb-app-main-grid"),
        activeWhen: (location) => [
            BASE_APP_PATH,
            `${BASE_APP_PATH}products`,
            `${BASE_APP_PATH}products/info/irservice`,
            `${BASE_APP_PATH}products/info/iposervice`,
            `${BASE_APP_PATH}products/info/bondexchange`,
            `${BASE_APP_PATH}products/info/dataCopyright`,
            `${BASE_APP_PATH}products/info/cfaemitent`,
            `${BASE_APP_PATH}products/info/cfainvestor`,
            `${BASE_APP_PATH}products/info/dataTicker`,
            `${BASE_APP_PATH}products/info/dataCalendar`,
            `${BASE_APP_PATH}products/info/inquiry`,
            `${BASE_APP_PATH}products/info/indexCalc`,
            `${BASE_APP_PATH}products/info/inav`,
            `${BASE_APP_PATH}products/info/indexBo`,
            `${BASE_APP_PATH}products/info/indexLog`,
            `${BASE_APP_PATH}products/info/brokerLog`,
        ].some((path) => location.pathname === path) &&
            excludeRegistrationSteps(location),
        customProps: {
            ...context,
            menu,
        },
    });
    registerApplication({
        name: "@platform/mb-app-aside-menu",
        app: () => System.import("@platform/mb-app-aside-menu"),
        activeWhen: (location) => excludeRegistrationSteps(location),
        customProps: {
            ...context,
            menu,
        },
    });
    const regex = new RegExp(`^(/pages/(.*))$`);
    registerApplication({
        name: "@platform/mb-app-iframe-viewer",
        app: () => System.import("@platform/mb-app-iframe-viewer"),
        activeWhen: (location) => regex.test(location.pathname),
        customProps: {
            ...context,
            menu,
        },
    });
    registerApplication({
        name: "@platform/mb-app-registration-steps",
        app: () => System.import("@platform/mb-app-registration-steps"),
        activeWhen: (location) => REGISTRATION_PATHS.some((path) => location.pathname.startsWith(path)),
        customProps: {
            ...context,
        },
    });
    registerApplication({
        name: "@platform/mb-app-registration",
        app: () => System.import("@platform/mb-app-registration"),
        activeWhen: (location) => REGISTRATION_PATHS.some((path) => location.pathname.startsWith(path)) &&
            location.pathname !== AUL_REG_PATH,
        customProps: {
            ...context,
        },
    });
}
